import { useParams, useNavigate } from "react-router-dom";
import { X, Ticket, History } from "lucide-react";
import { useTickets } from "@/context/TicketContext";
import { useSubscribers } from "@/context/SubscriberContext";
import { Header } from "@/components/Header";

const HistoryPage = () => {
  const { subscriberId } = useParams();
  const navigate = useNavigate();
  const { getClosedTicketsForSubscriber } = useTickets();
  const { subscribers } = useSubscribers();
  
  const subscriber = subscribers.find(s => s.id === parseInt(subscriberId));
  const closedTickets = getClosedTicketsForSubscriber(parseInt(subscriberId));

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  
  if (!subscriber) {
    return (
      <div className="app-container">
        <div className="device-page-header">
          <h1>Subscriber not found</h1>
          <button className="close-btn" onClick={() => navigate('/')}>
            <X size={24} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="history-page">
      <Header />

      {/* History Page Content */}
      <main className="main-content">
        <div className="device-page-header">
          <div>
            <h1 className="device-page-title">{subscriber.name} - Ticket History</h1>
            <p className="device-page-subtitle">{closedTickets.length} closed ticket{closedTickets.length !== 1 ? 's' : ''}</p>
          </div>
          <button className="close-btn" onClick={() => navigate('/')} data-testid="close-btn">
            <X size={24} />
          </button>
        </div>

        {closedTickets.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <History size={48} className="empty-state-icon" />
            <h3>No Ticket History</h3>
            <p>There are no closed tickets for this subscriber yet.</p>
            <button className="btn-back" onClick={() => navigate('/')}>
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="tickets-list" data-testid="history-list">
            {closedTickets.map(ticket => (
              <div key={ticket.ticketId} className="ticket-card history-card" data-testid={`history-card-${ticket.ticketId}`}>
                <div className="ticket-card-header history-header">
                  <div className="ticket-id-section">
                    <Ticket size={18} className="ticket-icon" />
                    <span className="ticket-id">{ticket.ticketId}</span>
                    <span className="ticket-status-badge closed">{ticket.status}</span>
                    <span className={`ticket-priority-badge ${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                  </div>
                  <div className="ticket-header-right">
                    <span className="ticket-date">Closed: {formatDateTime(ticket.closedAt)}</span>
                  </div>
                </div>
                <div className="ticket-card-body">
                  <div className="ticket-info-row">
                    <span className="ticket-label">Device:</span>
                    <span className="ticket-value">{ticket.deviceId}</span>
                    <span className="ticket-label">Type:</span>
                    <span className="ticket-value">{ticket.deviceType}</span>
                    <span className="ticket-label">Issue:</span>
                    <span className="ticket-value">{ticket.issueType}</span>
                  </div>
                  <div className="ticket-info-row">
                    <span className="ticket-label">Location:</span>
                    <span className="ticket-value">{ticket.location}</span>
                  </div>
                  <div className="ticket-info-row">
                    <span className="ticket-label">Assigned To:</span>
                    <span className="ticket-value">{ticket.assignedTo || 'Unassigned'}</span>
                    <span className="ticket-label">Created:</span>
                    <span className="ticket-value">{formatDateTime(ticket.createdAt)}</span>
                  </div>
                  {ticket.dispatchedAt && (
                    <div className="ticket-info-row">
                      <span className="ticket-label">Dispatched:</span>
                      <span className="ticket-value">{formatDateTime(ticket.dispatchedAt)}</span>
                    </div>
                  )}
                  <div className="ticket-closing-comments">
                    <span className="ticket-label">Resolution Notes:</span>
                    <p className="closing-comments-text">{ticket.closingComments}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
